/**
 * EntryCreateDialog — create a new keybook entry.
 *
 * Dynamic form based on credential type selection.
 */
import { useState, useEffect, useRef } from 'react'
import { KEY_NOTE_MAX } from '../core/entry-note.js'
import { createPortal } from 'react-dom'
import { useTranslation } from '@papan/i18n'
import { getApiBase } from '@papan/api-client'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import {
  X, ExternalLink, Eye, EyeOff, Plus, Search, ArrowLeft,
} from 'lucide-react'
import { PasswordInput, GithubIcon } from '@papan/core-ui/ui'
import type { KeybookEntryData, CredentialType } from '../core/types.js'
import { useBrand } from '@papan/brand/react'
import { SERVICES, searchServices, suggestedServices, ServiceGlyph, type ServiceDef } from './services.js'
import { RevokeGuidance } from './RevokeGuidance.js'
import { getFieldsForType } from './credential-type-ui.js'

interface EntryCreateDialogProps {
  tenantId: string
  onClose: () => void
  onSave: (
    label: string,
    data: KeybookEntryData,
    options?: { usernameHint?: string; provider?: string; noteOnly?: boolean },
  ) => Promise<void>
  /** Pre-fill for edit mode */
  editData?: KeybookEntryData
  editLabel?: string
  /** The entry's provider — picks the service shown in edit mode (several
   *  services share one credential shape). */
  editProvider?: string
  editTitle?: string
  /** Entry id — required when editing a github-type entry to enable
   *  the "Refresh metadata" button. Other types ignore it. */
  editEntryId?: string
  /** Renaming needs Manage; a Write-level edit keeps the label as it is
   *  (the label field is locked and never auto-filled). */
  labelLocked?: boolean
  /** Edit mode: the server refused the save because the key changed since it
   *  was opened (409 KEYBOOK_ENTRY_CHANGED). The draft stays; Reload loads the
   *  current value (`onReloadChanged`). */
  changedSinceOpened?: boolean
  onReloadChanged?: () => void
}

/** Types that don't have form fields — the entry is created by an
 *  external flow (OAuth install, etc.) instead of a Save click. */
const EXTERNAL_FLOW_TYPES = new Set<CredentialType>(['github'])

/** Per-type trade-off blurbs surfaced inline below the type dropdown.
 *  Honest tradeoffs — surface foot-guns clearly, don't bias toward the
 *  "easier" or "more secure" option. User picks based on their situation. */
const TYPE_HELP_TEXT: Partial<Record<CredentialType, string>> = {
  github: 'One click. Acts as you (commits, pushes show as your account). '
    + 'Not recommended to share with others — your activity gets attributed to them.',
  pat: 'Fine-grained control. Pick exact repos and read/write per permission. '
    + 'Best when you plan to share with teammates who shouldn\'t have their own GitHub access.',
}

// a type's fields: ./credential-type-ui (derived from the one type list, #1324 I1)

export function EntryCreateDialog({
  tenantId,
  onClose,
  onSave,
  editData,
  editLabel,
  editProvider,
  editTitle,
  editEntryId,
  labelLocked = false,
  changedSinceOpened = false,
  onReloadChanged,
}: EntryCreateDialogProps) {
  const { productName } = useBrand()
  const { t } = useTranslation('keybook')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const [label, setLabel] = useState(editLabel || '')
  // The key's note — saved inside the encrypted value (core/entry-note.ts).
  const [note, setNote] = useState(editData?.note ?? '')
  // An existing note is masked like the key's values until the person asks to see it.
  const [noteShown, setNoteShown] = useState(!editData?.note)
  const [credentialType, setCredentialType] = useState<CredentialType>(editData?.credentialType || 'password')
  /** Create flow is SERVICE-FIRST: step 1 searches the catalog, picking
   *  a service sets the shape + provider and advances to step 2 (the
   *  form). Edit mode skips step 1 (the shape is fixed). */
  const [service, setService] = useState<ServiceDef | null>(null)
  const [query, setQuery] = useState('')
  const [fields, setFields] = useState<Record<string, string>>(editData?.fields || {})
  /** For 'custom' type — user-defined field rows. Each row has a
   *  name, value, and a per-row sensitive flag (controls mask state
   *  in the detail panel and the input type here). */
  const [customRows, setCustomRows] = useState<Array<{ name: string; value: string; sensitive: boolean }>>(
    () => {
      if (editData?.credentialType === 'custom' && editData?.fields) {
        const meta = editData.fieldsMeta ?? {}
        return Object.entries(editData.fields).map(([name, value]) => ({
          name,
          value,
          sensitive: meta[name]?.sensitive ?? /password|key|token|secret/i.test(name),
        }))
      }
      return [{ name: '', value: '', sensitive: false }]
    },
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // PAT validation state — populated by the validate button after the
  // user pastes a token. Lets the form show "✅ access to: foo, bar"
  // before save, and feeds into the entry's display fields.
  const [patValidation, setPatValidation] = useState<{
    accountLogin: string
    accountType: 'User' | 'Organization'
    repositoryNames: string[]
    repositoryCount: number
    truncated: boolean
    repositorySelection?: 'all' | 'selected'
    scope?: string
  } | null>(null)
  const [patValidating, setPatValidating] = useState(false)

  // Refresh-metadata state for github-type edit. Stopgap until the
  // installation_repositories webhook lands; for now the user clicks
  // a button to re-fetch the repo list from GitHub.
  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<string | null>(null)

  const handleRefreshGithub = async () => {
    if (!editEntryId) return
    setRefreshing(true)
    setRefreshNote(null)
    try {
      const { keybookApi } = await import('../client/api.js')
      const result = await keybookApi.refreshGithubMetadata(editEntryId)
      setRefreshNote(`✓ Refreshed — ${result.repositoryCount} repo${result.repositoryCount === 1 ? '' : 's'}${result.repositoryTruncated ? ' (list truncated)' : ''}`)
    } catch (err: any) {
      setRefreshNote(`Refresh failed: ${err?.message ?? String(err)}`)
    } finally {
      setRefreshing(false)
    }
  }

  // File bindings are managed in the keybook detail panel, not in
  // this dialog. Bindings can be added or removed any time without
  // entering edit mode — see KeybookView for the chip list + add
  // button.

  const fieldDefs = getFieldsForType(credentialType)
  const isExternalFlow = EXTERNAL_FLOW_TYPES.has(credentialType)

  const handleTypeChange = (type: CredentialType) => {
    setCredentialType(type)
    setFields({}) // Reset fields when type changes
    setError('')
  }

  /** Step 1 → step 2: a chosen service sets the shape + provider. The Key
   *  Label is never pre-filled on a new entry (a filled field read as the
   *  entry's title) — the service shows only in its placeholder. */
  // In edit mode a "GitHub PAT: <login>" label is still the auto one, so
  // verifying a token of another account renames it.
  const autoLabel = useRef<string | null>(
    editData && editLabel && editLabel === `GitHub PAT: ${editData.fields?.account_login ?? ''}` ? editLabel : null,
  )
  /** The token the current `patValidation` describes — editing the token
   *  drops it, so an unverified token never saves with another's metadata. */
  const patValidatedFor = useRef<string | null>(null)
  // The token and label as they are NOW — a Verify that resolves after the
  // user changed the token (or typed a label) must not apply to them.
  const latestPat = useRef<string>('')
  latestPat.current = fields.pat?.trim() ?? ''
  const latestLabel = useRef(label)
  latestLabel.current = label
  useEffect(() => {
    if (patValidation && (fields.pat?.trim() ?? '') !== patValidatedFor.current) setPatValidation(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.pat])
  // Brand names stay as they are; the generic shapes and mechanism tags are
  // translated (keyed by service id / tag, the catalog's English as fallback).
  const svcLabel = (svc: ServiceDef) => svc.group === 'generic'
    ? t(`create.genericLabel.${svc.id}`, { defaultValue: svc.label })
    : svc.label
  const svcMech = (svc: ServiceDef) => t(`create.mech.${svc.mech.replace(/\s+/g, '')}`, { defaultValue: svc.mech })
  const pickService = (svc: ServiceDef) => {
    setService(svc)
    handleTypeChange(svc.credentialType)
    setPatValidation(null)
  }
  const backToSearch = () => {
    setService(null)
    setError('')
  }
  const searching = query.trim().length > 0
  /** "Show all": every supported key type, split Services / Generic, without
   *  having to type — the suggested list is only the common few. */
  const [showAllTypes, setShowAllTypes] = useState(false)
  const listingAll = searching || showAllTypes
  const results = searching ? searchServices(query, svc => [svcLabel(svc), svcMech(svc)])
    : showAllTypes ? SERVICES
    : suggestedServices()

  /** Validate a pasted PAT against GitHub. Populates patValidation
   *  with owner + repos so the form can show what the token grants
   *  before save. Failure modes (bad token, network error) surface
   *  inline. */
  const handleValidatePat = async () => {
    const token = fields.pat?.trim()
    if (!token) {
      setError('Paste a token first')
      return
    }
    setPatValidating(true)
    setError('')
    try {
      const { keybookApi } = await import('../client/api.js')
      const result = await keybookApi.validatePat({ provider: 'github', token })
      if (latestPat.current !== token) return // the token changed while verifying
      if (!result.ok) {
        setError(result.error || 'Token validation failed')
        setPatValidation(null)
      } else {
        setPatValidation({
          accountLogin: result.accountLogin!,
          accountType: result.accountType!,
          repositoryNames: result.repositoryNames!,
          repositoryCount: result.repositoryCount!,
          truncated: !!result.truncated,
          repositorySelection: result.repositorySelection,
          scope: result.scope,
        })
        // Rename only an edited entry's own auto label ("GitHub PAT: <old
        // login>"); a new entry's empty label stays empty (placeholder only).
        const cur = latestLabel.current
        if (!labelLocked && autoLabel.current && cur === autoLabel.current) {
          const l = `GitHub PAT: ${result.accountLogin}`
          autoLabel.current = l
          setLabel(l)
        }
        patValidatedFor.current = token
      }
    } catch (err: any) {
      if (latestPat.current !== token) return // a failure for a token already replaced
      setError(err.message || 'Token validation failed')
      setPatValidation(null)
    } finally {
      setPatValidating(false)
    }
  }

  /** Open the GitHub App install popup. The server creates the keybook
   *  entry directly from the OAuth callback; this dialog just kicks
   *  off the popup and closes itself once the install postMessage
   *  arrives (handled by the parent's listener).
   *
   *  If the user typed a label in the form, pipe it via the install
   *  state so the callback uses it instead of the auto-generated
   *  "GitHub: <account>" default. */
  const handleConnectGithub = () => {
    const apiBase = getApiBase()
    const origin = window.location.origin
    const labelParam = label.trim() ? `&label=${encodeURIComponent(label.trim())}` : ''
    const url = `${apiBase}/api/keybook/oauth/github/start?tenantId=${encodeURIComponent(tenantId)}&origin=${encodeURIComponent(origin)}${labelParam}`
    // Default features (with opener) — postMessage uses opener as a
    // best-effort fallback even though BroadcastChannel is the primary
    // delivery channel.
    window.open(url, 'papan-github-install', 'width=1020,height=940')
  }

  // When the install completes, close the dialog. The parent listener
  // refreshes the entry list separately.
  //
  // BroadcastChannel works across same-origin contexts and isn't
  // affected by GitHub COOP severing window.opener. postMessage stays
  // as a fallback for browsers without BroadcastChannel.
  useEffect(() => {
    if (!isExternalFlow) return

    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('papan-github-install')
      ch.onmessage = (ev) => {
        if (ev.data?.type === 'installed') onClose()
      }
    } catch { /* unsupported — postMessage will handle it */ }

    function onMessage(ev: MessageEvent) {
      if (ev.data?.type === 'keybook-github-installed') onClose()
    }
    window.addEventListener('message', onMessage)

    return () => {
      ch?.close()
      window.removeEventListener('message', onMessage)
    }
  }, [isExternalFlow, onClose])

  const handleSave = async () => {
    if (!label.trim()) {
      setError(t('create.labelRequired'))
      return
    }

    const isCustom = credentialType === 'custom'

    // External-flow edit (e.g. GitHub): the credential fields are
    // managed on the provider side, not here. Skip the "at least one
    // field" check — saving just persists label/file changes while
    // preserving the original installation metadata.
    if (!isExternalFlow && !isCustom) {
      const hasValues = fieldDefs.some(f => fields[f.name]?.trim())
      if (!hasValues) {
        setError('At least one field must be filled')
        return
      }
    }

    // Custom: at least one named field with a non-empty value.
    if (isCustom) {
      const filled = customRows.filter(r => r.name.trim() && r.value.trim())
      if (filled.length === 0) {
        setError('Add at least one field with a name and value')
        return
      }
      // Reject duplicate names — they'd silently overwrite each other.
      const names = filled.map(r => r.name.trim().toLowerCase())
      if (new Set(names).size !== names.length) {
        setError('Field names must be unique')
        return
      }
    }

    // For PAT entries, require the user to have validated the token
    // before save — guarantees we have owner/repo metadata and that
    // the token actually works. Without validation, save would store
    // an unverified blob.
    const patChanged = !editData || (fields.pat ?? '') !== (editData.fields?.pat ?? '')
    if (credentialType === 'pat' && !patValidation && patChanged) {
      setError('Validate the token first (click "Verify token")')
      return
    }

    setSaving(true)
    setError('')
    try {
      let data: KeybookEntryData
      // Provider comes from the picked service (Slack, GitLab, …) so the
      // fob shows the right connector; the PAT branch still forces
      // 'github' since that's the only verified provider.
      let provider: string | undefined = service?.provider
      if (isExternalFlow) {
        data = { fields: editData?.fields ?? {}, credentialType }
      } else if (isCustom) {
        // Build fields + fieldsMeta from the user-defined rows. Empty
        // rows already filtered above; here we just pack them up.
        const fieldsOut: Record<string, string> = {}
        const meta: Record<string, { sensitive?: boolean }> = {}
        for (const row of customRows) {
          const name = row.name.trim()
          if (!name || !row.value.trim()) continue
          fieldsOut[name] = row.value
          if (row.sensitive) meta[name] = { sensitive: true }
        }
        data = {
          fields: fieldsOut,
          credentialType,
          ...(Object.keys(meta).length > 0 && { fieldsMeta: meta }),
        }
      } else if (credentialType === 'pat') {
        // Embed validated metadata into the encrypted blob for display.
        // The pat itself is the only true secret here; the rest is
        // public-ish but goes through SSE for uniformity.
        const meta = patValidation
        data = {
          fields: {
            // Editing with the token unchanged keeps the stored owner / repo
            // metadata (it used to be wiped on every save).
            ...(editData && !meta && !patChanged ? editData.fields : {}),
            pat: fields.pat ?? '',
            ...(meta && {
              account_login: meta.accountLogin,
              account_type: meta.accountType,
              repository_count: String(meta.repositoryCount),
              repository_names: JSON.stringify(meta.repositoryNames),
              repository_truncated: meta.truncated ? '1' : '0',
              ...(meta.repositorySelection && { repository_selection: meta.repositorySelection }),
              ...(meta.scope && { scope: meta.scope }),
            }),
          },
          credentialType,
        }
        provider = 'github'
      } else {
        data = { fields, credentialType }
      }
      // Always sent (blank = no note), so an edit can remove one.
      data = { ...data, note }
      const usernameHint = isExternalFlow || credentialType === 'pat' || isCustom
        ? undefined
        : fields.username || fields.key?.slice(0, 8) || undefined
      // An edit that changed only the note goes through the note route: the secret is
      // untouched, so it isn't a new one. Strict — any field or type difference (or a
      // PAT re-verified) sends the whole value, so a real change is never dropped.
      // Same names in the same ORDER (a custom key shows its fields in stored order) and the same values.
      const sameFields = (a: Record<string, string> = {}, b: Record<string, string> = {}) => {
        const ka = Object.keys(a), kb = Object.keys(b)
        return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k])
      }
      const noteOnly = !!editData
        && (editData.note ?? '') !== note
        && data.credentialType === editData.credentialType
        && sameFields(data.fields, editData.fields)
        && sameFields(
          Object.fromEntries(Object.entries(data.fieldsMeta ?? {}).map(([k, m]) => [k, String(!!m?.sensitive)])),
          Object.fromEntries(Object.entries(editData.fieldsMeta ?? {}).map(([k, m]) => [k, String(!!m?.sensitive)])),
        )
        && !patValidation
      await onSave(label, data, {
        noteOnly,
        usernameHint,
        ...(provider && { provider }),
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  // No dismissing mid-save: a dialog closed and reopened while its write is in
  // flight would start a second save of the same key (the server's version check
  // refuses the loser, but this keeps the person from racing themselves).
  const dismiss = () => { if (!saving) onClose() }

  // Portaled: in-tree fixed overlays underlap the taskbar (ancestor
  // stacking context) — see KeybookView.
  return createPortal(
    <div className="kb-deck-portal fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={dismiss} />
      <div className={`${themeClass} ws-glass-dialog w-full max-w-md p-6 relative z-10 max-h-[90vh] overflow-y-auto`}>
        <button
          onClick={dismiss}
          disabled={saving}
          className="absolute top-4 right-4 themed-text-muted hover:themed-text"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-lg font-semibold themed-text mb-4">{editTitle || t('newEntry')}</h2>

        {changedSinceOpened && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/12 themed-text text-sm flex items-center gap-2" role="alert" data-testid="entry-changed-banner">
            <span className="flex-1 min-w-0">{t('entryChanged.message')}</span>
            {onReloadChanged && (
              <button type="button" onClick={onReloadChanged} disabled={saving} className="kbg-btn px-3 h-7 text-xs shrink-0" data-testid="entry-changed-reload">
                {t('entryChanged.reload')}
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!editData && !service ? (
          /* ── STEP 1 — service-first search. Type "Google" / "Postgres"
                / "API key"; pick a row to advance to the form. ── */
          <div className="space-y-3">
            <p className="text-sm themed-text-muted">{t('create.heading')}</p>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('create.searchPlaceholder')}
                className="themed-input w-full pl-9 pr-3 py-2 rounded-lg"
                data-testid="service-search"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-3">
              {(() => {
                // Empty query → one flat "Suggested" list (already ranked).
                // Typing, or "Show all" → the full catalog, split Services / Generic.
                const sections = listingAll
                  ? (['service', 'generic'] as const).map(g => ({
                      key: g, title: g === 'service' ? t('create.services') : t('create.generic'),
                      rows: results.filter(r => r.group === g),
                    }))
                  : [{ key: 'suggested', title: t('create.suggested'), rows: results }]
                return sections.map(({ key, title, rows }) => rows.length ? (
                  <div key={key}>
                    <div className="text-[10px] themed-text-muted uppercase tracking-wider px-1 mb-1">{title}</div>
                    <div className="space-y-0.5">
                      {rows.map(svc => (
                        <button
                          key={svc.id}
                          type="button"
                          data-testid="service-option"
                          onClick={() => pickService(svc)}
                          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors text-left cursor-pointer"
                        >
                          <ServiceGlyph svc={svc} />
                          <span className="flex-1 min-w-0 text-sm themed-text truncate">{svcLabel(svc)}</span>
                          <span className="kbg-pill text-[10px] px-1.5 py-0.5 shrink-0">{svcMech(svc)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null)
              })()}
              {searching && results.length === 0 && (
                <p className="text-xs themed-text-muted px-1 py-3">{t('create.noMatch')}</p>
              )}
            </div>
            {/* Below the list, not inside its scroll: always in view. */}
            {!searching && (
              <button
                type="button"
                onClick={() => setShowAllTypes(v => !v)}
                data-testid="service-show-all"
                className="w-full px-2 py-2 rounded-lg text-xs themed-text-secondary hover:themed-text hover:bg-white/5 transition-colors cursor-pointer"
              >
                {showAllTypes ? t('create.showSuggested') : t('create.showAll', { count: SERVICES.length })}
              </button>
            )}
          </div>
        ) : (
        <div className="space-y-4">
          {/* Picked-service header (create) or the fixed type (edit). */}
          <div>
            <label className="block text-sm themed-text-muted mb-1.5">{t('fields.type')}</label>
            {editData ? (() => {
              // Match the entry's provider first; else the GENERIC service of
              // this shape — never the first branded service that happens to
              // share it (a plain API key is not "OpenAI").
              const svc = (editProvider && SERVICES.find(s => s.provider === editProvider && s.credentialType === credentialType))
                || SERVICES.find(s => s.group === 'generic' && s.credentialType === credentialType)
              return (
                <span className="inline-flex items-center gap-2">
                  {svc ? <ServiceGlyph svc={svc} size={22} /> : null}
                  <span className="text-sm themed-text">{svc ? svcLabel(svc) : t(`credentialType.${credentialType}`, { defaultValue: credentialType })}</span>
                </span>
              )
            })() : service && (
              <div className="flex items-center gap-2.5">
                <ServiceGlyph svc={service} size={30} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm themed-text truncate">{svcLabel(service)}</span>
                  <span className="block text-[11px] themed-text-muted">{svcMech(service)}</span>
                </span>
                <button type="button" onClick={backToSearch} data-testid="service-change" className="kbg-btn h-7 px-2.5 text-xs shrink-0">
                  <ArrowLeft size={12} />
                  {t('create.change')}
                </button>
              </div>
            )}
            {/* Honest trade-off blurb — only for types with a real choice. */}
            {!editData && TYPE_HELP_TEXT[credentialType] && (
              <p className="mt-1.5 text-xs themed-text-muted leading-relaxed">
                {TYPE_HELP_TEXT[credentialType]}
              </p>
            )}
          </div>

          {/* Label. */}
          <div>
            <label className="block text-sm themed-text-muted mb-1">{t('fields.label')}</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={labelLocked}
              title={labelLocked ? t('labelNeedsManage') : undefined}
              className="themed-input w-full px-3 py-2 rounded-lg disabled:opacity-60"
              placeholder={
                isExternalFlow && !editData
                  ? 'Optional — defaults to "GitHub: <account>"'
                  : patValidation
                    ? t('create.labelPlaceholderService', { service: `GitHub PAT: ${patValidation.accountLogin}` })
                    // A brand names the example; a generic shape ("API Key") keeps the plain one.
                    : service && service.group !== 'generic'
                      ? t('create.labelPlaceholderService', { service: svcLabel(service) })
                      : t('create.labelPlaceholder')
              }
              autoFocus
            />
          </div>

          {/* Where to get the token, for the picked service. */}
          {service && (service.tokenUrl || service.tokenHelp) && !isExternalFlow && (
            <div className="text-xs themed-text-muted space-y-1">
              {service.tokenUrl && (
                <a
                  href={service.tokenUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 themed-text-secondary hover:themed-text underline underline-offset-2"
                >
                  {t('create.getToken', { service: service.label })}
                  <ExternalLink size={11} />
                </a>
              )}
              {service.tokenHelp && <p className="leading-relaxed">{t(`create.serviceHelp.${service.id}`, { defaultValue: service.tokenHelp })}</p>}
            </div>
          )}

          {/* GitHub authorize — create mode only. Body collapses to one
              big "Authorize on GitHub" CTA; the entry is created by
              the OAuth callback, not this dialog's Save button. */}
          {isExternalFlow && credentialType === 'github' && !editData && (
            <div className="kbg-well p-4 space-y-3">
              <div className="flex items-center gap-2 themed-text">
                <GithubIcon size={18} />
                <span className="font-medium">Authorize a GitHub account or organization</span>
              </div>
              <p className="text-xs themed-text-muted">
                Authorizes {productName} to access repositories on the account
                you choose. You pick which repositories to grant
                access to. Each authorized account gets its own entry
                — authorize once per account or organization.
              </p>
              <button
                type="button"
                onClick={handleConnectGithub}
                className="w-full themed-btn-primary px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                data-testid="github-authorize-button"
              >
                <GithubIcon size={14} />
                Authorize on GitHub
                <ExternalLink size={12} />
              </button>
            </div>
          )}

          {/* GitHub edit — read-only summary of what GitHub knows about
              this installation. Repos and permissions can only be
              changed on github.com (we just hold a reference), so this
              panel deep-links to the right management page. The label
              and file binding below are still editable. */}
          {isExternalFlow && credentialType === 'github' && editData && (() => {
            const f = editData.fields ?? {}
            const account = f.account_login || 'unknown'
            const accountType = f.account_type || 'User'
            const installationId = f.installation_id
            const repoSelection = f.repository_selection
            const repoCount = parseInt(f.repository_count || '0', 10)
            const repoNames: string[] = (() => {
              try { return JSON.parse(f.repository_names || '[]') } catch { return [] }
            })()
            const repoTruncated = f.repository_truncated === '1'
            const scopeLabel = repoSelection === 'all'
              ? `All repositories${repoCount ? ` (${repoCount} accessible)` : ''}`
              : repoSelection === 'selected'
                ? `Selected${repoCount ? ` (${repoCount} repo${repoCount === 1 ? '' : 's'})` : ''}`
                : 'Unknown'
            const manageUrl = installationId
              ? (accountType === 'Organization'
                  ? `https://github.com/organizations/${account}/settings/installations/${installationId}`
                  : `https://github.com/settings/installations/${installationId}`)
              : null
            return (
              <div className="kbg-well p-4 space-y-3">
                <div className="flex items-center gap-2 themed-text">
                  <GithubIcon size={18} />
                  <span className="font-medium">Authorized: {account}</span>
                  <span className="text-xs themed-text-muted">({accountType})</span>
                </div>
                <dl className="text-xs grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 themed-text-muted">
                  <dt>Account</dt>
                  <dd className="themed-text">{account}</dd>
                  <dt>Scope</dt>
                  <dd className="themed-text">{scopeLabel}</dd>
                  {installationId && <>
                    <dt>Installation</dt>
                    <dd className="themed-text font-mono">#{installationId}</dd>
                  </>}
                </dl>

                {repoNames.length > 0 && (
                  <div>
                    <div className="text-xs themed-text-muted mb-1.5">
                      Repositories {repoTruncated && <span className="text-[10px]">(first {repoNames.length})</span>}
                    </div>
                    <div className="rounded-md themed-bg-tertiary p-2 max-h-40 overflow-y-auto">
                      <ul className="text-xs themed-text font-mono space-y-0.5">
                        {repoNames.map(name => (
                          <li key={name} className="truncate" title={name}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 flex-wrap">
                  {manageUrl && (
                    <a
                      href={manageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs themed-text-secondary hover:themed-text underline underline-offset-2"
                    >
                      Manage on GitHub
                      <ExternalLink size={11} />
                    </a>
                  )}
                  {editEntryId && (
                    <button
                      type="button"
                      onClick={handleRefreshGithub}
                      disabled={refreshing}
                      className="themed-btn-secondary px-2.5 py-1 rounded-md text-xs disabled:opacity-40"
                      title="Re-fetch the accessible repo list from GitHub. Stopgap until the installation_repositories webhook lands."
                    >
                      {refreshing ? 'Refreshing…' : 'Refresh repo list'}
                    </button>
                  )}
                </div>
                {refreshNote && (
                  <p className={`text-xs ${refreshNote.startsWith('✓') ? 'themed-text-secondary' : 'text-red-400'}`}>
                    {refreshNote}
                  </p>
                )}
                <p className="text-xs themed-text-muted">
                  {t('revoke.githubRepos')}
                </p>
                {/* Deleting this entry doesn't revoke it on GitHub (since May —
                    a person's GitHub sign-in is theirs to revoke). */}
                <RevokeGuidance credentialType="github" provider="github" />
              </div>
            )
          })()}

          {/* Standard-flow body — credential fields. Label moved to the
              top of the column above. File bindings live in the right
              column. PAT verify panel only appears for credentialType
              ='pat' && !editData. */}
          {(!isExternalFlow || editData) && (<>

          {/* File bindings live in the right column now (Used by these
              folders or files). On small screens that column is below
              this one and collapsable. */}

          {/* Static fields — fixed schema per credentialType. Hidden
              for external-flow ('github' install) and 'custom' (user
              defines their own shape below). */}
          {!isExternalFlow && credentialType !== 'custom' && fieldDefs.map(field => (
            <div key={field.name}>
              <label className="block text-sm themed-text-muted mb-1">{field.label}</label>
              {field.inputType === 'textarea' ? (
                <textarea
                  value={fields[field.name] || ''}
                  onChange={(e) => setFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="themed-input w-full px-3 py-2 rounded-lg font-mono text-xs"
                  rows={4}
                />
              ) : field.inputType === 'password' ? (
                <PasswordInput
                  value={fields[field.name] || ''}
                  onChange={(e) => setFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="themed-input w-full px-3 py-2 rounded-lg"
                />
              ) : (
                <input
                  type={field.inputType}
                  value={fields[field.name] || ''}
                  onChange={(e) => setFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="themed-input w-full px-3 py-2 rounded-lg"
                />
              )}
            </div>
          ))}

          {/* Custom: user-defined field rows. Each row is a name +
              value, with a per-row "sensitive" flag controlling input
              type (password vs text) and the detail panel's mask. */}
          {credentialType === 'custom' && (
            <div className="space-y-2">
              <p className="text-xs themed-text-muted">
                Define your own fields. Toggle the eye icon to mark a
                field as sensitive (it will be masked in the detail panel).
              </p>
              {customRows.map((row, idx) => (
                <div key={idx} className="flex gap-1.5 items-start">
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => setCustomRows(prev => prev.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))}
                    className="themed-input flex-1 min-w-0 px-3 py-2 rounded-lg text-sm"
                    placeholder="field name"
                  />
                  {row.sensitive ? (
                    <PasswordInput
                      value={row.value}
                      onChange={(e) => setCustomRows(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                      className="themed-input flex-1 min-w-0 px-3 py-2 rounded-lg text-sm"
                    />
                  ) : (
                    <input
                      type="text"
                      value={row.value}
                      onChange={(e) => setCustomRows(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                      className="themed-input flex-1 min-w-0 px-3 py-2 rounded-lg text-sm"
                      placeholder="value"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setCustomRows(prev => prev.map((r, i) => i === idx ? { ...r, sensitive: !r.sensitive } : r))}
                    className="p-2 themed-text-muted hover:themed-text rounded-md flex-shrink-0"
                    title={row.sensitive ? 'Mark as non-sensitive' : 'Mark as sensitive'}
                    aria-label={row.sensitive ? 'Mark as non-sensitive' : 'Mark as sensitive'}
                  >
                    {row.sensitive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)}
                    disabled={customRows.length === 1}
                    className="p-2 themed-text-muted hover:text-red-400 rounded-md flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Remove field"
                    aria-label="Remove field"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setCustomRows(prev => [...prev, { name: '', value: '', sensitive: false }])}
                className="themed-btn-secondary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Add field
              </button>
            </div>
          )}

          {/* PAT-specific create-mode UI: link to GitHub PAT page +
              "Verify token" button + post-validation summary. Shown only
              when type=pat AND we're creating (edit mode reuses the
              entry's existing fields). */}
          {credentialType === 'pat' && (!editData || (fields.pat ?? '') !== (editData.fields?.pat ?? '')) && (
            <div className="space-y-2">
              <a
                href={`https://github.com/settings/personal-access-tokens/new?name=${encodeURIComponent(productName)}&description=${encodeURIComponent(`Read-only repo access for ${productName} agent`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs themed-text-secondary hover:themed-text underline underline-offset-2"
              >
                Open GitHub PAT page
                <ExternalLink size={11} />
              </a>
              <p className="text-xs themed-text-muted leading-relaxed">
                Recommended scope: <strong>Repository access → Only select repositories</strong>,
                <strong> Permissions → Contents: Read</strong> (or Read+Write).
                Then paste the token above and click Verify.
              </p>
              <button
                type="button"
                onClick={handleValidatePat}
                disabled={patValidating || !fields.pat?.trim()}
                className="themed-btn-secondary px-3 py-1.5 rounded-md text-xs"
              >
                {patValidating ? 'Verifying…' : patValidation ? 'Re-verify' : 'Verify token'}
              </button>
              {patValidation && (
                <div className="rounded-md themed-bg-tertiary p-3 space-y-1.5 text-xs">
                  <div className="themed-text font-medium">
                    ✓ Token verified
                  </div>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 themed-text-muted">
                    <dt>Owner</dt>
                    <dd className="themed-text">{patValidation.accountLogin} ({patValidation.accountType})</dd>
                    <dt>Repos</dt>
                    <dd className="themed-text">
                      {patValidation.repositoryCount}
                      {patValidation.repositorySelection === 'all' ? ' (all accessible)' : ''}
                      {patValidation.truncated ? ' — list truncated' : ''}
                    </dd>
                    {patValidation.scope && <>
                      <dt>Scope</dt>
                      <dd className="themed-text font-mono text-[11px]">{patValidation.scope}</dd>
                    </>}
                  </dl>
                  {patValidation.repositoryNames.length > 0 && (
                    <div className="rounded themed-bg-secondary p-2 max-h-24 overflow-y-auto">
                      <ul className="font-mono text-[11px] themed-text space-y-0.5">
                        {patValidation.repositoryNames.slice(0, 8).map(name => (
                          <li key={name} className="truncate" title={name}>{name}</li>
                        ))}
                        {patValidation.repositoryNames.length > 8 && (
                          <li className="themed-text-muted">…+{patValidation.repositoryNames.length - 8} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Note — encrypted with the value; only the current note is kept. */}
          {(!isExternalFlow || editData) && (
            <div>
              <label className="block text-sm themed-text-muted mb-1" htmlFor="keybook-entry-note">{t('fields.note')}</label>
              {!noteShown ? (
                <button
                  type="button"
                  data-testid="keybook-entry-note-show"
                  onClick={() => setNoteShown(true)}
                  className="themed-input w-full px-3 py-2 rounded-lg text-sm text-left flex items-center gap-2 themed-text-muted"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--input-bg) 20%, transparent)' }}
                >
                  <span className="flex-1 font-mono select-none">••••••••••••</span>
                  <Eye className="w-4 h-4" />{t('fields.showNote')}
                </button>
              ) : (
              <textarea
                id="keybook-entry-note"
                data-testid="keybook-entry-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={KEY_NOTE_MAX}
                rows={3}
                className="themed-input w-full px-3 py-2 rounded-lg text-sm resize-y"
                // Fainter than the key's own fields: the note is optional, secondary text.
                style={{ backgroundColor: 'color-mix(in srgb, var(--input-bg) 20%, transparent)' }}
                placeholder={t('create.notePlaceholder')}
              />
              )}
              <p className="text-xs themed-text-muted mt-1">{t('create.noteHint')}</p>
            </div>
          )}
          </>)}
        </div>
        )}

        <div className="flex gap-2 mt-6">
          <button
            onClick={dismiss}
            disabled={saving}
            className="themed-btn-secondary px-4 py-2 rounded-lg text-sm"
          >
            {isExternalFlow && !editData ? 'Close' : 'Cancel'}
          </button>
          {/* Save shows only on the form step (a service is picked, or
              edit mode) for standard types + external-flow edit. Hidden
              on the search step and for external-flow create (the
              provider's install action creates the entry). */}
          {(service || editData) && (!isExternalFlow || editData) && (
            <button
              onClick={handleSave}
              disabled={saving || !label.trim()}
              className="themed-btn-primary px-4 py-2 rounded-lg text-sm flex-1"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  , document.body)
}
